import React from 'react';
import { get } from 'lodash-es';
import { CARE_ORDER_BABY_TYPE } from '@/interface/domain/careOrderBaby';
import { useApiLoadingPool, usePreventInfiniteLoading, useRouter, useScrollToTop, } from '@/util/hook/custom';
import useDispatch from '@/util/hook/useDispatch';
import useMemo from '@/util/hook/useMemo';
import useSelector from '@/util/hook/useSelector';
import useState from '@/util/hook/useState';
import { selectLiffId } from '@/util/redux/auth';
import { careOrderBabyAPI } from '@/api/careClient';
import { COMMON_ROUTES } from '@/config/route';
import apiErrorCode from '@/constant/error/api_errorCode.json';
import { getParsedPlatformFeePercentage } from '../../util/helper/dataParser';
import { useCalculateTimeSlotFee } from '../../util/hook/useCalculateTimeSlotFee';
import { useGetCareClientVisitorProfile } from '../../util/hook/useGetCareClientVisitorProfile';
import { useGetRemoteConfig } from '../../util/hook/useGetRemoteConfig';
import { useTrackBrowserHistory } from '../../util/hook/useTrackBrowserHistory';
import { useValidateCareClientVisitorProfileAndRedirect } from '../../util/hook/useValidateCareClientVisitorProfileAndRedirect';
import { careOrderBabyUpdate } from '../../redux/module/careOrderBaby';
import { careOrderBabyFormResetTimeSelectionData, selectCareOrderBabyForm, } from '../../redux/module/careOrderBabyForm';
import { selectAllDayPlatformFeePercentage, selectMaxServiceDays, selectPartTimePlatformFeePercentage, } from '../../redux/module/remoteConfig';
import { trackerIsCareClientVisitorProfileInitializedReset } from '../../redux/module/tracker';
import View from './View';
import { APP } from '../../config/app';
import { MODAL_TYPE } from '../../config/modal';
import { CareOrderBabyFormForCareOrderBabyInitDConverter } from '../../util/dataConverter/CareOrderBabyFormForCareOrderBabyInitDConverter';
import { CareOrderBabyFormUConverter } from '../../util/uiConverter/CareOrderBabyForm';
import { TimeSlotFeeDataUConverter } from '../../util/uiConverter/TimeSlotFeeData';
export default function Controller() {
    const { isLoading, setLoading, setApiProcessingPool, setApiDonePool } = useApiLoadingPool(true);
    const [modalType, setModalType] = useState(MODAL_TYPE.CLOSE);
    const _id = useSelector(selectLiffId);
    const careOrderBabyForm = useSelector(selectCareOrderBabyForm);
    const maxServiceDays = useSelector(selectMaxServiceDays);
    const allDayPlatformFeePercentage = useSelector(selectAllDayPlatformFeePercentage);
    const partTimePlatformFeePercentage = useSelector(selectPartTimePlatformFeePercentage);
    const timeSlotList = careOrderBabyForm.timeSlotList;
    const isAllDay = careOrderBabyForm.initialType === CARE_ORDER_BABY_TYPE.ALL_DAY;
    const { navigate, navigateError } = useRouter();
    const dispatch = useDispatch();
    useTrackBrowserHistory();
    useGetRemoteConfig({ setApiProcessingPool, setApiDonePool });
    useGetCareClientVisitorProfile({ setApiProcessingPool, setApiDonePool });
    useValidateCareClientVisitorProfileAndRedirect({ redirectRoute: null });
    useScrollToTop();
    usePreventInfiniteLoading({ setApiProcessingPool, setApiDonePool });
    const { timeSlotFeeData } = useCalculateTimeSlotFee({
        setApiProcessingPool,
        setApiDonePool,
    });
    const careOrderBabyFormForDetailConfirmationPage = useMemo(() => {
        const data = CareOrderBabyFormUConverter.getDataForDetailConfirmationPage({
            careOrderBabyForm,
            maxServiceDays,
        });
        return data;
    }, [careOrderBabyForm, maxServiceDays]);
    const timeSlotFeeDataForDetailConfirmationPage = useMemo(() => {
        if (!timeSlotFeeData)
            return null;
        const data = TimeSlotFeeDataUConverter.getDataForDetailConfirmationPage({
            timeSlotFeeData,
            timeSlotCount: timeSlotList.length,
        });
        return data;
    }, [timeSlotFeeData]);
    const parsedAllDayPlatformFeePercentage = useMemo(() => {
        if (!allDayPlatformFeePercentage)
            return '';
        return getParsedPlatformFeePercentage({
            platformFeePercentage: allDayPlatformFeePercentage,
        });
    }, [allDayPlatformFeePercentage]);
    const parsedPartTimePlatformFeePercentage = useMemo(() => {
        if (!partTimePlatformFeePercentage)
            return '';
        return getParsedPlatformFeePercentage({
            platformFeePercentage: partTimePlatformFeePercentage,
        });
    }, [partTimePlatformFeePercentage]);
    const careOrderBabyFormForCareOrderBabyInit = useMemo(() => {
        const { data } = new CareOrderBabyFormForCareOrderBabyInitDConverter(careOrderBabyForm);
        return data;
    }, [careOrderBabyForm]);
    // event handlers
    const handleBackButtonClick = () => {
        navigate(APP.ROUTES.DATE_AND_TIME);
    };
    const handleForwardButtonClick = async () => {
        setLoading(true);
        const apiErrorCodeMappingKey = 'client/careOrderBaby/init';
        const careOrderInitParams = { _id, ...careOrderBabyFormForCareOrderBabyInit };
        const res = await careOrderBabyAPI.Init(careOrderInitParams, setApiProcessingPool, setApiDonePool);
        const errorTypes = get(res, 'data.errorTypes', []);
        if (res.success) {
            dispatch(trackerIsCareClientVisitorProfileInitializedReset());
            const careOrderBabyCode = get(res, 'data.code', '');
            const careOrderBabyExpirationTime = get(res, 'data.expirationTime', '');
            dispatch(careOrderBabyUpdate({
                code: careOrderBabyCode,
                expirationTime: careOrderBabyExpirationTime,
            }));
            navigate(APP.ROUTES.TRANSFER_INSTRUCTION);
        }
        else if (errorTypes.includes('timeSlotList')) {
            setModalType(MODAL_TYPE.CARE_ORDER_BABY_INIT_ERROR_BAD_PARAMS_TIME_SLOT_LIST);
        }
        else if (res.errorCode === apiErrorCode[apiErrorCodeMappingKey].careClientVisitorExpired.errorCode) {
            setModalType(MODAL_TYPE.CARE_ORDER_BABY_INIT_ERROR_CARE_CLIENT_VISITOR_EXPIRED);
        }
        else {
            navigateError(res.errorCode, COMMON_ROUTES.API_ERROR);
        }
        setLoading(true);
    };
    const handleConfirmInvalidTimeSlotList = () => {
        dispatch(careOrderBabyFormResetTimeSelectionData());
        setModalType(MODAL_TYPE.CLOSE);
        navigate(APP.ROUTES.DATE_AND_TIME);
    };
    const handleConfirmExpiredCareClientVisitor = () => {
        window.location.reload();
    };
    return (<View 
    // data
    isLoading={isLoading} isAllDay={isAllDay} isForwardButtonAvailable careOrderBabyFormForDetailConfirmationPage={careOrderBabyFormForDetailConfirmationPage} timeSlotFeeDataForDetailConfirmationPage={timeSlotFeeDataForDetailConfirmationPage} parsedAllDayPlatformFeePercentage={parsedAllDayPlatformFeePercentage} parsedPartTimePlatformFeePercentage={parsedPartTimePlatformFeePercentage} modalType={modalType} 
    // func
    handleBackButtonClick={handleBackButtonClick} handleForwardButtonClick={handleForwardButtonClick} handleConfirmInvalidTimeSlotList={handleConfirmInvalidTimeSlotList} handleConfirmExpiredCareClientVisitor={handleConfirmExpiredCareClientVisitor}/>);
}
